/**
 * BlockedOverlays — three full-screen blurred overlays that cover the chat
 * message list when one of three blocking conditions is active. At most one
 * branch renders at a time; the caller passes which.
 *
 * Flutter source of truth (`chat_screen.dart:1010-1113`):
 *  - `currentUserBlockedWidget` (1010-1042): copy `"Your account is blocked!"`.
 *  - `currentUserBlockedByPeerWidget` (1044-1076): copy `"Conversation not available!"`.
 *  - `peerUserBlockedWidget` (1078-1113): copy `"${peerName}'s account is blocked"`,
 *    with an extra outer `EdgeInsets.all(16.0)` padding around the centered text.
 *
 * Each Flutter branch is a `SizedBox` filling the screen with a `Stack` of:
 *   1. `BackdropFilter(blur sigmaX=10, sigmaY=10)` + a 30%-alpha light overlay color.
 *   2. A centered title pulled from the canonical `AppStyles.overlayTitleStyle`
 *      (20pt, normal weight) with `bottom: 16` padding.
 *
 * Deviations from Flutter UI (allowed per `presentation.md`):
 *  - Uses CSS `backdrop-filter: blur(...)` rather than `BackdropFilter` — the
 *    web-native equivalent. Sigma 10 ≈ 10px blur radius.
 *  - The 30%-alpha light overlay color uses `theme.loadingOverlay`, the
 *    project's standard 30%-alpha overlay token (matches the Flutter intent;
 *    avoids hardcoding a one-off color).
 *
 * l10n: `overlay.accountBlocked` / `overlay.conversationNotAvailable` /
 *       `overlay.peerAccountBlocked` (the last one uses a `{peerName}`
 *       placeholder). Sourced from `useLocale().l10n.chat.overlay.*`.
 */

import React from 'react';
import { BlockedOverlayVariant } from '@/domain/enums/chat/blockedOverlayVariant';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface BlockedOverlaysProps {
  /** Which overlay to show, or `null` to render nothing. */
  variant: BlockedOverlayVariant | null;
  /**
   * Peer's first name. Required only when `variant === BlockedOverlayVariant.peerUserBlocked` —
   * mirrors the Flutter `_peerUser!.firstName` access at `chat_screen.dart:1102`.
   * Passing `null` for the other two variants is fine; the component does
   * not read this prop for them.
   */
  peerName: string | null;
}

interface OverlayShellProps {
  /** Text to render centered over the blurred backdrop. */
  title: string;
  /**
   * Optional outer padding (used by `peerUserBlocked` to mirror the Flutter
   * `EdgeInsets.all(16.0)` wrapper around the centered text — see
   * `chat_screen.dart:1096-1108`).
   */
  outerPadding?: number;
  /**
   * Coarse block-state token spread onto the backdrop as `data-qa-status` so
   * the qa-tester can assert which block variant is active. Caller-supplied.
   */
  qaStatus: string;
}

/**
 * Full-screen absolute-positioned blurred backdrop with a centered title.
 * Shared by all three variants — the only differences between them are the
 * title string and the optional outer padding.
 */
const OverlayShell: React.FC<OverlayShellProps> = ({
  title,
  outerPadding,
  qaStatus,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Flutter uses `sigmaX/Y: 10` (`DimensionConstants.overlayBlur{X,Y}`).
  // The matching responsive token is `responsive.chat.overlayBlur` (=10 on
  // both platforms); CSS `blur(Xpx)` is the closest equivalent of Flutter's
  // `BackdropFilter` sigma.
  const blurPx = responsive.chat.overlayBlur;

  return (
    <div
      {...qaAttr({ id: 'chat-blocked-overlay', status: qaStatus })}
      style={{
        position: 'absolute',
        inset: 0,
        // 30%-alpha overlay color — matches the Flutter intent at
        // `chat_screen.dart:1024` (backgroundColorLight × .3 alpha) without
        // pinning a one-off hex literal.
        backgroundColor: theme.loadingOverlay,
        backdropFilter: `blur(${blurPx}px)`,
        WebkitBackdropFilter: `blur(${blurPx}px)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: outerPadding ?? 0,
        boxSizing: 'border-box',
        // The chat page is the only consumer; sit above the message list
        // but below sheets/modals. No explicit z-index baseline needed —
        // overlay only renders when active and is owned by the chat scroll
        // container, so it inherits stacking from its parent.
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          // Flutter wraps the title in `Padding(bottom: 16)` to nudge it
          // slightly above geometric center (`chat_screen.dart:1030`).
          paddingBottom: responsive.spacing.lg,
        }}
      >
        <span
          style={{
            // `AppStyles.overlayTitleStyle` is 20px, normal weight,
            // text color.
            fontSize: responsive.font['2xl'],
            fontWeight: responsive.fontWeight.regular,
            color: theme.text.primary,
            textAlign: 'left',
            // The Flutter title has a single-row drop shadow via
            // `appElevationToShadow[0]`. Skipping it on web — drop shadow
            // is not load-bearing for legibility against the blurred
            // backdrop, which is itself the contrast surface.
          }}
        >
          {title}
        </span>
      </div>
    </div>
  );
};

export const BlockedOverlays: React.FC<BlockedOverlaysProps> = ({
  variant,
  peerName,
}) => {
  const { l10n } = useLocale();
  const chat = l10n.chat;
  const responsive = useResponsiveDesign();

  if (variant === null) {
    return null;
  }

  // --- Branch 1: current user is globally blocked ---
  if (variant === BlockedOverlayVariant.currentUserBlocked) {
    return (
      <OverlayShell
        title={chat.overlay.accountBlocked}
        qaStatus="current-user-blocked"
      />
    );
  }

  // --- Branch 2: current user has been blocked by the peer ---
  if (variant === BlockedOverlayVariant.currentUserBlockedByPeer) {
    return (
      <OverlayShell
        title={chat.overlay.conversationNotAvailable}
        qaStatus="blocked-me"
      />
    );
  }

  // --- Branch 3: peer user is globally blocked ---
  // Flutter wraps this variant in an extra `EdgeInsets.all(16.0)` so the
  // (typically longer) "${name}'s account is blocked" line doesn't kiss the
  // viewport edges on narrow widths. Mirror that here.
  const resolvedPeerName = peerName ?? '';
  const peerTitle = chat.overlay.peerAccountBlocked.replace(
    '{peerName}',
    resolvedPeerName,
  );
  return (
    <OverlayShell
      title={peerTitle}
      outerPadding={responsive.spacing.lg}
      qaStatus="blocked-by-me"
    />
  );
};
